import { nextSeriesForm } from './creationDefaults';
import { useEffect, useMemo, useState, useRef } from 'react';
import type { SeriesCreateRequest } from '../../api/types';
import { GameModeNames, ServerRegionNames } from '../../domain/enums';
import {
  SERIES_FORMATS,
  seriesFormatInfo,
  type SeriesFormat,
} from '../../domain/series';
import { useConfig, type Team } from '../../store/config';
import { Button, Checkbox, Dialog, Field, Select, TextInput } from '../../ui';
import {
  MAX_PLAYERS_PER_TEAM,
  SERIES_TIMEOUT_DEFAULT_MINUTES,
  buildSeriesRequest,
  initialSeriesFormValues,
  validateSeriesForm,
  type SeriesFormValues,
  type SeriesVisibility,
} from './seriesForm';

function teamHint(teams: Team[], teamId: string): string | undefined {
  const team = teams.find((candidate) => candidate.id === teamId);
  if (!team) return undefined;
  const count = team.playerSteamIds.length;
  return `${count} player${count === 1 ? '' : 's'} will be invited`;
}

function TeamSelect({
  id,
  value,
  teams,
  onChange,
}: {
  id: string;
  value: string;
  teams: Team[];
  onChange: (teamId: string) => void;
}) {
  return (
    <Select
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">Pick a team</option>
      {teams.map((team) => (
        <option key={team.id} value={team.id}>
          {team.name || 'Unnamed team'} ({team.playerSteamIds.length})
        </option>
      ))}
    </Select>
  );
}

function SectionTitle({ children }: { children: string }) {
  return (
    <h3 className="mb-3 font-sans text-xs font-semibold tracking-wide text-gold-300 uppercase">
      {children}
    </h3>
  );
}

export default function SeriesFormDialog({
  open,
  submitting,
  onClose,
  onSubmit,
}: {
  open: boolean;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (
    values: SeriesFormValues,
    payload: SeriesCreateRequest,
    afterCreated?: () => void,
  ) => void;
}) {
  const { config } = useConfig();
  const [values, setValues] = useState<SeriesFormValues>(() =>
    initialSeriesFormValues(config),
  );
  const [touched, setTouched] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const submitted = useRef<{ hash: string; id: string } | null>(null);

  useEffect(() => {
    if (!open) return;
    setValues(initialSeriesFormValues(config));
    setTouched(false);
    setAdvanced(false);
  }, [open]);

  const errors = useMemo(
    () => validateSeriesForm(values, config.teams),
    [values, config.teams],
  );
  const errorFor = (field: keyof SeriesFormValues) =>
    touched
      ? errors.find((error) => error.field === field)?.message
      : undefined;

  const set = <K extends keyof SeriesFormValues>(
    key: K,
    value: SeriesFormValues[K],
  ) => setValues((current) => ({ ...current, [key]: value }));

  const format = seriesFormatInfo(values.format);
  const resuming = Number(values.game_number) > 1;
  const advancedHasErrors = errors.some((error) =>
    [
      'teams_fill_timeout_minutes',
      'between_games_timeout_minutes',
      'decision_timeout_minutes',
      'game_duration_timeout_minutes',
      'webhook_url',
      'game_number',
      'initial_radiant_wins',
    ].includes(error.field),
  );

  const changeFormat = (next: SeriesFormat) => {
    const maxGames = seriesFormatInfo(next).maxGames;
    setValues((current) => ({
      ...current,
      format: next,
      game_number: Math.min(Number(current.game_number) || 1, maxGames),
    }));
  };

  const handleSubmit = (another = false) => {
    setTouched(true);
    if (errors.length > 0) {
      if (advancedHasErrors) setAdvanced(true);
      return;
    }
    const payload = buildSeriesRequest(values, config);
    const hash = JSON.stringify(payload);
    if (!submitted.current || submitted.current.hash !== hash)
      submitted.current = { hash, id: crypto.randomUUID() };
    payload.request_id = submitted.current.id;
    onSubmit(
      values,
      payload,
      another
        ? () => {
            submitted.current = null;
            setValues(nextSeriesForm(values));
            setTouched(false);
          }
        : undefined,
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title="Create series"
      description="One bot runs the whole series: a fresh lobby for every game, the same two teams invited again each time."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={submitting} onClick={() => handleSubmit(true)}>
            Create and add another
          </Button>
          <Button
            variant="primary"
            loading={submitting}
            onClick={() => handleSubmit()}
          >
            Create series
          </Button>
        </>
      }
    >
      <section>
        <SectionTitle>Series</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Series name"
            error={errorFor('game_name')}
            hint="Used as the lobby name of every game"
          >
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                value={values.game_name}
                onChange={(event) => set('game_name', event.target.value)}
                placeholder="GG League - Finals"
              />
            )}
          </Field>

          <Field label="Format" hint={format.hint}>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={values.format}
                onChange={(event) =>
                  changeFormat(event.target.value as SeriesFormat)
                }
              >
                {SERIES_FORMATS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} · {option.hint}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Radiant team"
            error={errorFor('radiantTeamId')}
            hint={teamHint(config.teams, values.radiantTeamId)}
          >
            {({ id }) => (
              <TeamSelect
                id={id}
                value={values.radiantTeamId}
                teams={config.teams}
                onChange={(teamId) =>
                  setValues((v) => ({
                    ...v,
                    radiantTeamId: teamId,
                    radiantStarters:
                      config.teams
                        .find((t) => t.id === teamId)
                        ?.playerSteamIds.slice(0, v.players_per_team) ?? [],
                  }))
                }
              />
            )}
          </Field>

          <Field
            label="Dire team"
            error={errorFor('direTeamId')}
            hint={teamHint(config.teams, values.direTeamId)}
          >
            {({ id }) => (
              <TeamSelect
                id={id}
                value={values.direTeamId}
                teams={config.teams}
                onChange={(teamId) =>
                  setValues((v) => ({
                    ...v,
                    direTeamId: teamId,
                    direStarters:
                      config.teams
                        .find((t) => t.id === teamId)
                        ?.playerSteamIds.slice(0, v.players_per_team) ?? [],
                  }))
                }
              />
            )}
          </Field>

          <Field
            label="Players per team"
            error={errorFor('players_per_team')}
            hint="A game starts once both sides have this many players seated"
          >
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                type="number"
                min={1}
                max={MAX_PLAYERS_PER_TEAM}
                value={values.players_per_team}
                onChange={(event) =>
                  set('players_per_team', Number(event.target.value))
                }
              />
            )}
          </Field>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {(['radiant', 'dire'] as const).map((side) => {
            const selected =
              values[side === 'radiant' ? 'radiantStarters' : 'direStarters'] ??
              [];
            const team = config.teams.find(
              (t) =>
                t.id ===
                values[side === 'radiant' ? 'radiantTeamId' : 'direTeamId'],
            );
            const field =
              side === 'radiant' ? 'radiantStarters' : 'direStarters';
            return (
              <fieldset key={side}>
                <legend className="text-sm font-medium">
                  {side === 'radiant' ? 'Team A' : 'Team B'} starters (
                  {selected.length}/{values.players_per_team})
                </legend>
                {team?.playerSteamIds.map((steamId) => (
                  <label
                    key={steamId}
                    className="mt-2 flex items-center gap-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(steamId)}
                      onChange={(e) =>
                        set(
                          field,
                          e.target.checked
                            ? [...selected, steamId]
                            : selected.filter((id) => id !== steamId),
                        )
                      }
                    />
                    {config.players.find((p) => p.steamId === steamId)?.name ||
                      steamId}
                  </label>
                ))}
                <label className="mt-3 block text-xs">
                  Ready-check captain
                  <Select
                    value={
                      values[
                        side === 'radiant' ? 'radiantCaptain' : 'direCaptain'
                      ] ?? ''
                    }
                    onChange={(e) =>
                      set(
                        side === 'radiant' ? 'radiantCaptain' : 'direCaptain',
                        e.target.value,
                      )
                    }
                  >
                    <option value="">Any seated starter</option>
                    {selected.map((id) => (
                      <option key={id} value={id}>
                        {config.players.find((p) => p.steamId === id)?.name ||
                          id}
                      </option>
                    ))}
                  </Select>
                </label>
                {errorFor(field) && (
                  <p className="text-xs text-warn">{errorFor(field)}</p>
                )}
              </fieldset>
            );
          })}
        </div>
        <Field label="Roster policy">
          {({ id }) => (
            <Select
              id={id}
              value={values.rosterPolicy ?? 'open'}
              onChange={(e) =>
                set(
                  'rosterPolicy',
                  e.target.value as SeriesFormValues['rosterPolicy'],
                )
              }
            >
              <option value="open">Open</option>
              <option value="organizer_approval">Organizer approval</option>
              <option value="locked_lineup">Locked lineup</option>
            </Select>
          )}
        </Field>
        {config.teams.length === 0 && (
          <p className="mt-3 text-xs text-warn">
            No teams yet. Create both teams on the Teams page first; a series
            always invites two teams.
          </p>
        )}

        <p className="mt-3 text-xs text-ink-faint">
          Teams keep their score whichever side they end up on: Dota&apos;s coin
          toss picks side and first pick before every game.
        </p>
      </section>

      <section className="mt-6 border-t border-line pt-5">
        <SectionTitle>Lobby</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Server region">
            {({ id }) => (
              <Select
                id={id}
                value={values.server_region}
                onChange={(event) =>
                  set('server_region', Number(event.target.value))
                }
              >
                {Object.entries(ServerRegionNames).map(([value, name]) => (
                  <option key={value} value={value}>
                    {name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Game mode">
            {({ id }) => (
              <Select
                id={id}
                value={values.game_mode}
                onChange={(event) =>
                  set('game_mode', Number(event.target.value))
                }
              >
                {Object.entries(GameModeNames).map(([value, name]) => (
                  <option key={value} value={value}>
                    {name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="League ID" error={errorFor('league_id')}>
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                type="number"
                min={0}
                value={values.league_id}
                onChange={(event) =>
                  set('league_id', Number(event.target.value))
                }
              />
            )}
          </Field>

          <Field
            label="Password"
            hint="Leave empty for an open lobby. Players can ask the bot with !password"
          >
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                value={values.password}
                onChange={(event) => set('password', event.target.value)}
              />
            )}
          </Field>

          <Field label="Visibility">
            {({ id }) => (
              <Select
                id={id}
                value={values.visibility}
                onChange={(event) =>
                  set('visibility', event.target.value as SeriesVisibility)
                }
              >
                <option value="public">Public</option>
                <option value="friends">Friends</option>
                <option value="unlisted">Unlisted</option>
              </Select>
            )}
          </Field>

          <div className="flex flex-col justify-end gap-2 pb-1">
            <Checkbox
              label="All chat"
              checked={values.allchat}
              onChange={(event) => set('allchat', event.target.checked)}
            />
          </div>
        </div>
      </section>

      <section className="mt-6 rounded-lg border border-line bg-surface-2/50 p-4 text-xs text-ink-dim">
        <p className="mb-2 font-semibold text-ink">How a series runs</p>
        <ol className="list-decimal space-y-1 pl-4">
          <li>
            Both teams get an invite; players sit on their side. Missing players
            are re-invited every 90 s.
          </li>
          <li>
            Once both sides are full, one player per side types{' '}
            <code className="text-ink">!ready</code> in lobby chat.
          </li>
          <li>
            Dota&apos;s coin toss picks side and first pick, then the bot
            launches the game.
          </li>
          <li>
            When the game ends the bot closes the lobby, opens the next one and
            invites everyone again, until the series is decided.
          </li>
        </ol>
        <p className="mt-2">
          The lobby card shows the score and offers{' '}
          <span className="text-ink">Cancel series</span> at any point.
        </p>
      </section>

      <section className="mt-6 border-t border-line pt-5">
        <button
          type="button"
          onClick={() => setAdvanced((show) => !show)}
          aria-expanded={advanced}
          className="text-xs text-ink-dim underline-offset-4 hover:text-ink hover:underline"
        >
          {advanced
            ? 'Hide advanced settings'
            : 'Advanced settings: timeouts, webhook, resume'}
        </button>

        {advanced && (
          <div className="mt-4 space-y-6">
            <div>
              <SectionTitle>Timeouts (minutes)</SectionTitle>
              <p className="mb-3 text-xs text-ink-faint">
                Leave empty to keep the server defaults. Each bound applies to
                one game.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Teams fill"
                  error={errorFor('teams_fill_timeout_minutes')}
                  hint="How long to wait for both teams to seat"
                >
                  {({ id, describedBy }) => (
                    <TextInput
                      id={id}
                      aria-describedby={describedBy}
                      type="number"
                      min={1}
                      inputMode="numeric"
                      value={values.teams_fill_timeout_minutes}
                      onChange={(event) =>
                        set('teams_fill_timeout_minutes', event.target.value)
                      }
                      placeholder={String(
                        SERIES_TIMEOUT_DEFAULT_MINUTES.teams_fill,
                      )}
                    />
                  )}
                </Field>
                <Field
                  label="Ready check"
                  error={errorFor('between_games_timeout_minutes')}
                  hint="Longest break before !ready from both sides"
                >
                  {({ id, describedBy }) => (
                    <TextInput
                      id={id}
                      aria-describedby={describedBy}
                      type="number"
                      min={1}
                      inputMode="numeric"
                      value={values.between_games_timeout_minutes}
                      onChange={(event) =>
                        set('between_games_timeout_minutes', event.target.value)
                      }
                      placeholder={String(
                        SERIES_TIMEOUT_DEFAULT_MINUTES.between_games,
                      )}
                    />
                  )}
                </Field>
                <Field
                  label="Side pick"
                  error={errorFor('decision_timeout_minutes')}
                  hint="Coin toss: side and first pick choice"
                >
                  {({ id, describedBy }) => (
                    <TextInput
                      id={id}
                      aria-describedby={describedBy}
                      type="number"
                      min={1}
                      inputMode="numeric"
                      value={values.decision_timeout_minutes}
                      onChange={(event) =>
                        set('decision_timeout_minutes', event.target.value)
                      }
                      placeholder={String(
                        SERIES_TIMEOUT_DEFAULT_MINUTES.decision,
                      )}
                    />
                  )}
                </Field>
                <Field
                  label="Game length"
                  error={errorFor('game_duration_timeout_minutes')}
                  hint="Longest a single game may run"
                >
                  {({ id, describedBy }) => (
                    <TextInput
                      id={id}
                      aria-describedby={describedBy}
                      type="number"
                      min={1}
                      inputMode="numeric"
                      value={values.game_duration_timeout_minutes}
                      onChange={(event) =>
                        set('game_duration_timeout_minutes', event.target.value)
                      }
                      placeholder={String(
                        SERIES_TIMEOUT_DEFAULT_MINUTES.game_duration,
                      )}
                    />
                  )}
                </Field>
              </div>
            </div>

            <div>
              <SectionTitle>Webhook</SectionTitle>
              <p className="mb-3 text-xs text-ink-faint">
                The API posts every series event (created, game launched, game
                finished, completed, cancelled) to this URL.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Webhook URL" error={errorFor('webhook_url')}>
                  {({ id, describedBy }) => (
                    <TextInput
                      id={id}
                      aria-describedby={describedBy}
                      value={values.webhook_url}
                      onChange={(event) =>
                        set('webhook_url', event.target.value)
                      }
                      placeholder="https://example.org/hooks/series"
                    />
                  )}
                </Field>
                <Field
                  label="Webhook secret"
                  hint="Optional; sent with every event"
                >
                  {({ id, describedBy }) => (
                    <TextInput
                      id={id}
                      aria-describedby={describedBy}
                      type="password"
                      autoComplete="off"
                      value={values.webhook_secret}
                      onChange={(event) =>
                        set('webhook_secret', event.target.value)
                      }
                    />
                  )}
                </Field>
              </div>
            </div>

            <div>
              <SectionTitle>Resume an interrupted series</SectionTitle>
              <p className="mb-3 text-xs text-ink-faint">
                Start later than game 1 and enter the score so far. Wins belong
                to the team, not the side: count the Radiant team&apos;s wins
                even for games it played on Dire.
              </p>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Start at game" error={errorFor('game_number')}>
                  {({ id, describedBy }) => (
                    <Select
                      id={id}
                      aria-describedby={describedBy}
                      value={values.game_number}
                      onChange={(event) =>
                        set('game_number', Number(event.target.value))
                      }
                    >
                      {Array.from(
                        { length: format.maxGames },
                        (_, index) => index + 1,
                      ).map((game) => (
                        <option key={game} value={game}>
                          Game {game}
                          {game === 1 ? ' (new series)' : ''}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field
                  label="Radiant team wins"
                  error={errorFor('initial_radiant_wins')}
                  hint={resuming ? undefined : 'Only for a resumed series'}
                >
                  {({ id, describedBy }) => (
                    <TextInput
                      id={id}
                      aria-describedby={describedBy}
                      type="number"
                      min={0}
                      disabled={!resuming}
                      value={values.initial_radiant_wins}
                      onChange={(event) =>
                        set('initial_radiant_wins', Number(event.target.value))
                      }
                    />
                  )}
                </Field>
                <Field label="Dire team wins">
                  {({ id }) => (
                    <TextInput
                      id={id}
                      type="number"
                      min={0}
                      disabled={!resuming}
                      value={values.initial_dire_wins}
                      onChange={(event) =>
                        set('initial_dire_wins', Number(event.target.value))
                      }
                    />
                  )}
                </Field>
              </div>
            </div>
          </div>
        )}
      </section>
    </Dialog>
  );
}
